<template>
   <div class="mt-10 bg-gray-200 p-4 rounded-lg shadow-md flex flex-col">
      <h2 class="col-span-4 xs:col-span-3">
         {{
            guestIndex === 0
               ? 'Primary guest information'
               : 'Guest ' + guestIndex + ' information'
         }}
      </h2>
      <div
         class="flex-grid md:flex md:space-x-4 mt-4"
         v-for="field in model"
         :key="field.id"
      >
         <div
            class="grow"
            v-if="field.name !== 'primary_guest'"
         >
            <div
               v-if="field.type === 'text-input' || field.type === 'email'"
            >
               <InputLabel
                  :class="{ required: field.required }"
                  :for="field.name"
                  :value="field.label"
               />
               <InputText
                  size="small"
                  :disabled="readOnly"
                  :id="field.name"
                  :type="field.type === 'email' ? 'email' : 'text'"
                  class="mt-1 block w-full"
                  v-model="field.value"
                  :required="field.required"
                  autocomplete="off"
                  autofocus
               />
            </div>
            <div v-if="field.type === 'phone'">
               <InputLabel
                  :class="{ required: field.required }"
                  :for="field.name"
                  :value="field.label"
               />
               <PhoneInput
                  :disabled="readOnly"
                  v-model="field.value"
               />
            </div>
            <div v-if="field.type === 'text-area'">
               <InputLabel
                  :class="{ required: field.required }"
                  :for="field.name"
                  :value="field.label"
               />
               <Textarea
                  size="small"
                  :disabled="readOnly"
                  v-model="field.value"
                  rows="4"
                  class="w-full mt-1 rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-primary-color sm:text-sm sm:leading-6"
               />
            </div>
            <div
               v-if="field.type === 'dropdown'"
            >
               <InputLabel
                  :class="{ required: field.required }"
                  :for="field.name"
                  :value="field.label"
               />
               <div class="w-full">
                  <Select
                     :disabled="readOnly"
                     size="small"
                     v-model="field.value"
                     :options="field.options"
                     option-label="name"
                     option-value="id"
                     class="w-full md:w-56"
                     pt:option:class="!text-sm"
                     pt:emptyMessage:class="!text-sm"
                  />
               </div>
            </div>
            <div
               v-if="
                  field.type === 'multiselect'
               "
            >
               <InputLabel
                  :class="{ required: field.required }"
                  :for="field.name"
                  :value="field.label"
               />
               <div class="w-full">
                  <MultiSelect
                     :disabled="readOnly"
                     size="small"
                     v-model="field.value"
                     :options="field.options"
                     option-label="name"
                     option-value="id"
                     class="w-full md:w-56"
                     pt:option:class="!text-sm"
                     pt:emptyMessage:class="!text-sm"
                  />
               </div>
            </div>
            <div v-if="field.type === 'checkboxes'">
               <InputLabel
                  :class="{ required: field.required }"
                  :for="field.name"
                  F
                  :value="field.label"
               />
               <div class="flex flex-col mt-2">
                  <div
                     class="flex items-center mt-2"
                     v-for="(option, key) in field.options"
                     :key="key"
                  >
                     <Checkbox
                        binary
                        size="small"
                        :disabled="readOnly"
                        v-model="option.value"
                     />
                     <span class="ms-2 text-sm text-gray-600">{{
                        option.name
                     }}</span>
                  </div>
               </div>
            </div>
            <div v-if="field.type === 'date'">
               <InputLabel
                  :class="{ required: field.required }"
                  :for="field.name"
                  :value="field.label"
               />
               <DatePicker
                  :disabled="readOnly"
                  :name="field.name"
                  v-model="field.value"
                  :required="field.required"
                  size="small"
                  class="w-full"
                  show-icon
                  icon-display="input"
                  date-format="dd-mm-yy"
                  :max-date="new Date()"
               />
            </div>

            <div v-if="field.type === 'time'">
               <InputLabel
                  :class="{ required: field.required }"
                  :for="field.name"
                  :value="field.label"
               />
               <DatePicker
                  :disabled="readOnly"
                  :name="field.name"
                  v-model="field.value"
                  :required="field.required"
                  time-only
                  show-icon
                  icon-display="input"
                  size="small"
                  hour-format="24"
                  fluid
               >
                  <template #inputicon="slotProps">
                     <i
                        class="pi pi-clock"
                        @click="slotProps.clickCallback"
                     />
                  </template>
               </DatePicker>
            </div>
            <InputError
               class="mt-2"
               :message="
                  isArray(errors[`guests.${guestIndex}.${field.name}`])
                     ? errors[`guests.${guestIndex}.${field.name}`][0]
                     : errors[`guests.${guestIndex}.${field.name}`]
               "
            />
         </div>
      </div>
   </div>
</template>

<script setup>
   import InputLabel from '@/Components/Forms/InputLabel.vue';
   import { defineEmits, defineModel } from 'vue';

   import MultiSelect from 'primevue/multiselect';

   import { isArray } from 'lodash';
   import InputError from '@/Components/Forms/InputError.vue';
   import PhoneInput from '@/Components/Forms/PhoneInput.vue';
   import { DatePicker, InputText } from 'primevue';
   import Checkbox from 'primevue/checkbox';
   import Select from 'primevue/select';
   import Textarea from 'primevue/textarea';

   defineProps({
      errors: Object,
      guestIndex: Number,
      readOnly: {
         type: Boolean,
         default: false,
      },
   });

   const model = defineModel({
      type: Object,
      required: true,
   });

   const emit = defineEmits(['removeGuest']);

</script>

<style scoped></style>
